import { GeneralLedgerTransaction } from 'src/app/domain/financial-statement/models/common/GeneralLedgerTransaction';
import { FinancialStatementAccountDTO } from '../DTOs/common/FinancialStatementAccountDTO';
import { FinancialStatementItem } from './FinancialStatementItem';
import { FsSums } from '../../utils/common/FsSums';

export class FinancialStatementAccount {
  id?: number;
  code?: string;
  name?: string;
  isManual?: boolean;
  glTransactions?: GeneralLedgerTransaction[];
  financialStatementItems?: FinancialStatementItem[];
  // below properties are not saved in backend
  isSelected?: boolean;
  isSelectedForDelete?: boolean;
  sums: FsSums;
  quarterlyTransactionsMap = new Map<string, GeneralLedgerTransaction[]>();
  filteredTransactions: GeneralLedgerTransaction[];
  showInTb = true;

  constructor(id?: number, code?: string, name?: string, isManual?: boolean,
    glTransactions?: GeneralLedgerTransaction[], items?: FinancialStatementItem[], isSelected?: boolean, sums?: FsSums) {
    this.id = id;
    this.code = code;
    this.name = name;
    this.isManual = isManual;
    this.glTransactions = glTransactions;
    this.financialStatementItems = items;
    this.isSelected = isSelected;
    this.sums = sums ?? new FsSums();
  }

  static fromDTO(dto: FinancialStatementAccountDTO): FinancialStatementAccount {
    return new FinancialStatementAccount(
      dto.id,
      dto.code,
      dto.name,
      true, // isManualAccount
      [], // glTransactions
      [], // maGlTransactions
      false, // isSelected
      new FsSums(),
    );
  }

  toDTO(): FinancialStatementAccountDTO {
    return new FinancialStatementAccountDTO(
      this.id, this.code, this.name, this.isManual,
      this.glTransactions ? this.glTransactions.map((t) => t.id) : [],
      this.financialStatementItems ? this.financialStatementItems.map((i) => i.id) : [],
    );
  }

  findAccountSums(fsYear: number, startDate?: Date, endDate?: Date): void {
    if (!this.glTransactions) {
      return;
    }
    this.glTransactions.forEach((transaction) => transaction.isSelected = false);
    if (startDate && endDate) {
      this.filteredTransactions = this.glTransactions.filter((transaction) =>
        transaction.transactionDate >= startDate && transaction.transactionDate <= endDate);
    } else {
      this.filteredTransactions = this.glTransactions;
    }
    this.sums.resetAllSums();
    this.filteredTransactions.forEach((transaction) => {
      this.sums.balance += (transaction.debit - transaction.credit);
      if (transaction.isManualEntry) {
        this.sums.manualCredit += transaction.credit;
        this.sums.manualDebit += transaction.debit;
      }
    });
    this.sums.balance = (Number)(this.sums.balance.toFixed(3));
    const glTransactionsForAccount = this.filteredTransactions.filter((data) => !(data?.isManualEntry));
    const sumFields = ['sumITP', 'sumITPrevYear', 'sumITYearBeforePrev'];
    sumFields.forEach((field, index) => {
      this.filterTransactionsByYear(glTransactionsForAccount, (fsYear - index), fsYear).forEach((data) => {
        this.sums[field] += (data.debit - data.credit);
      });
    });

    this.quarterlyTransactionsMap.forEach((transactions, quarter) => {
      this.sums.quarterlySums.set(quarter, transactions.reduce((total, transaction) =>
        total + (transaction.debit - transaction.credit), 0));
    });

    this.sums.quarterlySums.forEach((sum, quarter) => {
      const year = quarter.substring(3, 8);
      this.sums.yearlySums.set(year, this.sums.yearlySums.has(year) ? this.sums.yearlySums.get(year) + sum : sum);
    });

    this.sums.calculateYearlySums();
  }

  groupByQuarters(): void {
    this.quarterlyTransactionsMap = new Map();
    this.glTransactions.forEach((element) => {
      if (!element) {
        return;
      }
      const date = new Date(element?.transactionDate);
      const month = date.getMonth();
      let quarterKey = '';
      switch (true) {
        case (month <= 2):
          quarterKey = 'Q1';
          break;
        case (month > 2 && month <= 5):
          quarterKey = 'Q2';
          break;
        case (month > 5 && month <= 8):
          quarterKey = 'Q3';
          break;
        case (month > 8):
          quarterKey = 'Q4';
          break;
      }
      quarterKey = quarterKey + ' ' + date.getFullYear();
      if (!element?.transactionDate) {
        quarterKey = 'No date';
      }
      if (this.quarterlyTransactionsMap.has(quarterKey)) {
        this.quarterlyTransactionsMap.get(quarterKey).push(element);
      } else {
        this.quarterlyTransactionsMap.set(quarterKey, [element]);
      }
    });
    const sortedArray = Array.from(this.quarterlyTransactionsMap.entries()).sort((a, b) => {
      const aQuarter = (a[0].substring(1, 2));
      const aYear = (a[0].substring(3));
      const bQuarter = (b[0].substring(1, 2));
      const bYear = (b[0].substring(3));

      if (aYear !== bYear) {
        return aYear.localeCompare(bYear);
      }
      return aQuarter.localeCompare(bQuarter);
    });
    this.quarterlyTransactionsMap = new Map<string, GeneralLedgerTransaction[]>(sortedArray);
  }

  private filterTransactionsByYear(transactions: GeneralLedgerTransaction[], filterYear: number, fsYear: number):
  GeneralLedgerTransaction[] {
    const prevYear = fsYear - 1;
    let transactionsByYear = transactions; // leave as is if year is fsYear
    if (filterYear === prevYear) {
      // remove transactions where year equal fsYear leaving everything from previous year or earlier
      transactionsByYear = transactions.filter((transaction) =>
        Number(String(transaction.transactionDate).slice(0, 4)) !== fsYear);
    } else if (filterYear !== fsYear) {
      // remove transactions where year equal fsYear or previous year leaving only what came before
      transactionsByYear = transactions.filter((transaction) =>
        Number(String(transaction.transactionDate).slice(0, 4)) !== fsYear &&
        Number(String(transaction.transactionDate).slice(0, 4)) !== prevYear);
    }
    return transactionsByYear;
  }
}
